import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Lock, Monitor } from 'lucide-react';
import apiClient from '@/lib/api';
import { Input } from '@/components/ui/input';

const LoginScreen = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data } = await apiClient.post('/auth/login', { email, password });
      localStorage.setItem('ainaa_token', data.token);
      if (data.refresh_token) localStorage.setItem('ainaa_refresh_token', data.refresh_token);
      localStorage.setItem('ainaa_admin_logged_in', 'true');
      onLogin(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  if (isMobile) {
    return (
      <div className="min-h-screen bg-[#EDECEA] flex items-center justify-center px-6" data-testid="admin-mobile-block">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm text-center">
          <div className="border-2 border-[#0F1B2D] bg-white p-8">
            <div className="w-14 h-14 bg-[#0F1B2D] flex items-center justify-center mx-auto mb-6">
              <Monitor size={24} className="text-[#C4A35A]" />
            </div>
            <h2 className="font-['Playfair_Display'] text-xl font-bold text-[#0F1B2D] mb-3">Desktop Required</h2>
            <p className="text-sm text-[#9B9589] leading-relaxed mb-6">
              The AINAA Admin Console is optimised for desktop screens. Please switch to a laptop or desktop to access the admin panel.
            </p>
            <div className="border-t border-[#D8D3CB] pt-4">
              <p className="text-[10px] text-[#D8D3CB] uppercase tracking-wider font-bold">Minimum 768px viewport</p>
            </div>
          </div>
          <div className="mt-4">
            <Link to="/" className="text-xs font-medium text-[#9B9589] hover:text-[#0F1B2D] transition-colors">Back to site</Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#EDECEA] flex items-center justify-center px-4" data-testid="admin-login">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
        <div className="border-2 border-[#0F1B2D] bg-white p-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-[#0F1B2D] flex items-center justify-center">
              <Lock size={18} className="text-[#C4A35A]" />
            </div>
            <div>
              <p className="font-['Playfair_Display'] text-lg font-semibold text-[#0F1B2D]">AINAA</p>
              <p className="text-[9px] uppercase tracking-[0.15em] text-[#9B9589] font-semibold">Admin Panel</p>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#9B9589] font-bold block mb-2">Email</label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-ainaa h-11" placeholder="admin@ainaa.co" required data-testid="login-email" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#9B9589] font-bold block mb-2">Password</label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="input-ainaa h-11" placeholder="Enter password" required data-testid="login-password" />
            </div>
            {error && <p className="text-sm text-red-600 font-medium" data-testid="login-error">{error}</p>}
            <button type="submit" disabled={loading} className="btn-ainaa-primary w-full py-3 text-sm font-semibold uppercase tracking-wider" data-testid="login-submit">
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        </div>
        <div className="text-center mt-4">
          <Link to="/" className="text-xs font-medium text-[#9B9589] hover:text-[#0F1B2D] transition-colors">Back to site</Link>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginScreen;
